import ROLE from '../enums/roles'
import router from '../router/index'
import store from '../store'

export function useRoutRedirect() {
  /**
   * Check if user has any permissions at all
   */
  const hasAnyPermissions = () => {
    const permissions = store.state.globalComponents?.permissions?.permissionGroups || {}

    // Check if permissionGroups is empty or all groups are empty
    const allPermissions = Object.values(permissions).flat()
    return allPermissions.length > 0
  }

  /**
   * Get first available route based on user permissions
   */
  const getFirstAvailableRoute = (role) => {
    const permissions = store.state.globalComponents?.permissions?.permissionGroups || {}

    // Helper function to check if user has a specific permission
    const hasPermission = (permName) => {
      const [group] = permName.split('.')
      return permissions[group]?.some((p) => p.name === permName) || false
    }

    // Priority order of routes to check
    const routePriority = [
      {
        name: `${role}/dashboard`,
        permission: 'init.load-data',
        condition: true,
      },
      {
        name: `${role}/revenue-dashboard`,
        permission: 'dashboard-tab.view',
        condition: hasPermission('dashboard-tab.view'),
      },
      {
        name: `${role}/all`,
        permission: 'member.view',
        condition: hasPermission('member.view'),
      },
      {
        name: `${role}/opportunities`,
        permission: 'opportunity.view',
        condition: hasPermission('opportunity.view') && !hasPermission('member.view'),
      },
      {
        name: 'activity',
        permission: 'activity.list',
        condition: hasPermission('activity.list'),
      },
      {
        name: 'schwab',
        permission: 'schwab.view-data',
        condition: hasPermission('schwab.view-data'),
      },
      {
        name: 'annuity',
        permission: 'client-report.view-annuities',
        condition: hasPermission('client-report.view-annuities'),
      },
    ]

    // Find the first available route
    for (const route of routePriority) {
      if (route.condition) {
        return { name: route.name }
      }
    }

    // Fallback to basic dashboard
    return { name: `${role}/dashboard` }
  }

  const routRedirect = async ({ role, userId }) => {
    // Early validation: if no role provided, redirect to login
    if (!role || role === 'undefined' || role === 'null') {
      router.push({ path: '/login' })
      return
    }

    // Normalize role to lowercase for comparison
    const roleLower = role?.toLowerCase() || ''

    // Special case for CLIENT role
    if (roleLower === ROLE.CLIENT.toLowerCase()) {
      // Check if userId exists before redirecting
      if (!userId || userId === 'undefined' || userId === 'null') {
        router.push({ path: '/login' }).catch((err) => console.error('[routRedirect] Navigation error:', err))
        return
      }
      try {
        router.push({ name: `client/member-details`, params: { id: userId } })
      } catch (err) {
        router.push({ path: '/login' })
      }
      return
    }

    // Special case for LEAD role
    if (roleLower === ROLE.LEAD.toLowerCase()) {
      try {
        router.push({ name: 'lead/dashboard' })
      } catch (err) {
        router.push({ path: '/login' })
      }
      return
    }

    // Check if user has zero permissions (excluding CLIENT and LEAD)
    // Note: For custom roles, permissions should still be loaded from the API
    if (!hasAnyPermissions()) {
      router.push({ name: 'no-permissions' })
      return
    }

    // Normalize role to lowercase first

    // Map unknown roles to supported roles
    const standardRoles = ['advisor', 'admin', 'ceo', 'support', 'assistant', 'other']
    let mappedRole

    // Assistant role uses advisor routes
    if (roleLower === 'assistant') {
      mappedRole = 'advisor'
    } else if (standardRoles.includes(roleLower)) {
      // Role is a known standard role, use it as-is (already lowercase)
      mappedRole = roleLower
    } else {
      // Unknown role - map to 'other'
      mappedRole = 'other'
    }

    // For all other roles, use permission-based routing
    const defaultRoute = getFirstAvailableRoute(mappedRole)
    try {
      await router.push(defaultRoute)
    } catch (err) {
      router.push({ path: '/login' })
    }
  }

  return { routRedirect }
}
